import type { BackendSymbolDef, BackendMechanics } from '@core/types/backend-game.types';

/**
 * Known golden symbol definitions.
 * These IDs appear in API bet responses but are missing from backend config symbols[].
 */
export const GOLDEN_SYMBOL_DEFS: BackendSymbolDef[] = [
  // Golden high symbols (gold skin of H-1 through H-4)
  { id: 55, code: 'H-1-G', name: 'Golden H-1', category: 'golden' },
  { id: 56, code: 'H-2-G', name: 'Golden H-2', category: 'golden' },
  { id: 57, code: 'H-3-G', name: 'Golden H-3', category: 'golden' },
  { id: 58, code: 'H-4-G', name: 'Golden H-4', category: 'golden' },
  // Golden low/normal symbols (gold skin of L-1 through L-4)
  { id: 65, code: 'L-1-G', name: 'Golden L-1', category: 'golden' },
  { id: 66, code: 'L-2-G', name: 'Golden L-2', category: 'golden' },
  { id: 67, code: 'L-3-G', name: 'Golden L-3', category: 'golden' },
  { id: 68, code: 'L-4-G', name: 'Golden L-4', category: 'golden' },
];

/**
 * Augment a symbols array with golden variant definitions when the game supports them.
 * - Returns original array unmodified if mechanics.goldenSymbols is not true
 * - Skips golden defs whose ID already exists in the symbols array (dedup)
 */
export function augmentSymbolsWithGolden(
  symbols: BackendSymbolDef[],
  mechanics?: BackendMechanics
): BackendSymbolDef[] {
  if (!mechanics?.goldenSymbols) return symbols;

  const existingIds = new Set(symbols.map(s => s.id));
  const missing = GOLDEN_SYMBOL_DEFS.filter(g => !existingIds.has(g.id));

  if (missing.length === 0) return symbols;
  return [...symbols, ...missing];
}

/**
 * Remap normal symbol code prefix from "N" to "L" (e.g. "N1" → "L1").
 * Golden symbols already use "L-" prefix, so this makes the naming consistent.
 */
function remapCode(code: string): string {
  return code.replace(/^N(\d)/, 'L$1');
}

/**
 * Remap all "N" prefix symbol codes to "L" in a symbols array.
 * Returns a new array with remapped codes; originals are not mutated.
 */
export function remapNormalSymbolCodes(symbols: BackendSymbolDef[]): BackendSymbolDef[] {
  return symbols.map(s => {
    const newCode = remapCode(s.code);
    if (newCode === s.code) return s;
    return { ...s, code: newCode, name: s.name.replace(/^N(\d)/, 'L$1') };
  });
}
